/**
 * InstanceDataContext provides a robust data management layer for instance data with:
 * - Smart caching system with configurable expiration (default: 5 minutes)
 * - Request deduplication to prevent redundant API calls
 * - Loading and error state tracking per instance
 * - Automatic cache invalidation
 * - Multiple hooks for different access patterns:
 *   - useInstanceById: Load and access a specific instance
 *   - useCurrentInstanceData: Access the currently selected instance
 */
import * as React from "react";
import { APIOutput, Instance } from "@amzn/ask-legal-domain";
import { AppContext } from "../setup/context";
import { useInstanceState } from "../components/instance/instance-store";

interface CachedInstance {
  instance: Instance;
  timestamp: number;
}

interface InstanceDataContextType {
  getInstanceById: (instanceId: string) => Instance | undefined;
  getCurrentInstance: () => Instance | undefined;
  getCurrentInstanceId: () => string | undefined;
  isLoading: (instanceId: string) => boolean;
  hasError: (instanceId: string) => boolean;
  loadInstance: (instanceId: string) => Promise<Instance | undefined>;
  refreshCurrentInstance: () => Promise<Instance | undefined>;
  invalidateCache: (instanceId?: string) => void;
  instanceCache: Record<string, Instance>;
}

const CACHE_EXPIRY_MS = 5 * 60 * 1000;
const instanceCache = new Map<string, CachedInstance>();
const pendingRequests = new Map<string, Promise<Instance | undefined>>();

const InstanceDataContext = React.createContext<InstanceDataContextType | null>(null);

interface InstanceDataProviderProps {
  children: React.ReactNode;
  cacheDuration?: number;
}

export const InstanceDataProvider: React.FC<InstanceDataProviderProps> = ({
  children,
  cacheDuration = CACHE_EXPIRY_MS
}) => {
  const [loadingStates, setLoadingStates] = React.useState<Record<string, boolean>>({});
  const [errorStates, setErrorStates] = React.useState<Record<string, boolean>>({});
  const [cacheVersion, setCacheVersion] = React.useState(0);
  const context = React.useContext(AppContext);

  const isCacheValid = (cachedData: CachedInstance): boolean => {
    const now = Date.now();
    return (now - cachedData.timestamp) < cacheDuration;
  };

  const loadInstance = React.useCallback(async (instanceId: string, forceRefresh = false): Promise<Instance | undefined> => {
    if (!instanceId) return undefined;
    const cachedData = instanceCache.get(instanceId);
    if (!forceRefresh && cachedData && isCacheValid(cachedData)) {
      return cachedData.instance;
    }
    const existingRequest = pendingRequests.get(instanceId);
    if (existingRequest) {
      setLoadingStates(prev => ({ ...prev, [instanceId]: true }));
      try {
        return await existingRequest;
      } finally {
        setLoadingStates(prev => ({ ...prev, [instanceId]: false }));
      }
    }

    setLoadingStates(prev => ({ ...prev, [instanceId]: true }));
    setErrorStates(prev => ({ ...prev, [instanceId]: false }));

    const fetchPromise = (async () => {
      try {
        const response = await context.getInstanceAPI().load(instanceId);
        const output = APIOutput.fromRaw<Instance>(response.data);
        if (output.isOk()) {
          const instance = output.data;
          instanceCache.set(instanceId, {
            instance,
            timestamp: Date.now()
          });
          setCacheVersion(v => v + 1);
          return instance;
        }
        throw new Error(`Failed to load instance ${instanceId}`);
      } catch (error) {
        setErrorStates(prev => ({ ...prev, [instanceId]: true }));
        console.error(`Error loading instance ${instanceId}:`, error);
        return undefined;
      } finally {
        setLoadingStates(prev => ({ ...prev, [instanceId]: false }));
        pendingRequests.delete(instanceId);
      }
    })();
    pendingRequests.set(instanceId, fetchPromise);
    return fetchPromise;
  }, [context, cacheDuration]);

  const invalidateCache = React.useCallback((instanceId?: string) => {
    if (instanceId) {
      instanceCache.delete(instanceId);
    } else {
      instanceCache.clear();
    }
    setCacheVersion(v => v + 1);
  }, []);

  const isLoading = React.useCallback((instanceId: string): boolean => {
    return Boolean(loadingStates[instanceId]);
  }, [loadingStates]);

  const hasError = React.useCallback((instanceId: string): boolean => {
    return Boolean(errorStates[instanceId]);
  }, [errorStates]);

  const getInstanceById = React.useCallback((instanceId: string): Instance | undefined => {
    const cachedData = instanceCache.get(instanceId);
    return cachedData?.instance;
  }, [cacheVersion]);

  const getCurrentInstanceId = React.useCallback((): string | undefined => {
    const { instanceId } = useInstanceState();
    return instanceId || undefined;
  }, []);

  const getCurrentInstance = React.useCallback((): Instance | undefined => {
    const { instanceId } = useInstanceState();
    if (!instanceId) return undefined;
    const cachedData = instanceCache.get(instanceId);
    return cachedData?.instance;
  }, [cacheVersion]);

  const refreshCurrentInstance = React.useCallback(async (): Promise<Instance | undefined> => {
    const { instanceId } = useInstanceState();
    if (!instanceId) return undefined;
    return await loadInstance(instanceId, true);
  }, [loadInstance]);

  const getInstanceObjectCache = React.useCallback((): Record<string, Instance> => {
    const result: Record<string, Instance> = {};
    instanceCache.forEach((cachedData, id) => {
      result[id] = cachedData.instance;
    });
    return result;
  }, [cacheVersion]);

  const contextValue = React.useMemo(() => ({
    getInstanceById,
    getCurrentInstance,
    getCurrentInstanceId,
    isLoading,
    hasError,
    loadInstance,
    refreshCurrentInstance,
    invalidateCache,
    instanceCache: getInstanceObjectCache()
  }), [
    getInstanceById,
    getCurrentInstance,
    getCurrentInstanceId,
    isLoading,
    hasError,
    loadInstance,
    refreshCurrentInstance,
    invalidateCache,
    getInstanceObjectCache
  ]);

  return (
    <InstanceDataContext.Provider value={contextValue}>
      {children}
    </InstanceDataContext.Provider>
  );
};

export const useInstanceDataContext = () => {
  const context = React.useContext(InstanceDataContext);
  if (!context) {
    throw new Error("useInstanceDataContext must be used within an InstanceDataProvider");
  }
  return context;
};

export const useInstanceById = (instanceId?: string) => {
  const { getInstanceById, loadInstance, isLoading, hasError } = useInstanceDataContext();
  const instance = instanceId ? getInstanceById(instanceId) : undefined;
  React.useEffect(() => {
    if (instanceId && !instance && !isLoading(instanceId)) {
      loadInstance(instanceId).catch(console.error);
    }
  }, [instanceId, instance, loadInstance, isLoading]);
  return {
    instance,
    isLoading: instanceId ? isLoading(instanceId) : false,
    hasError: instanceId ? hasError(instanceId) : false
  };
};

export const useCurrentInstanceData = () => {
  const {
    getCurrentInstance,
    getCurrentInstanceId,
    isLoading,
    hasError,
    loadInstance,
    refreshCurrentInstance
  } = useInstanceDataContext();

  const instanceId = getCurrentInstanceId();
  const instance = getCurrentInstance();

  React.useEffect(() => {
    if (instanceId && !instance && !isLoading(instanceId)) {
      loadInstance(instanceId).catch(console.error);
    }
  }, [instanceId, instance, isLoading, loadInstance]);

  return {
    instance,
    instanceId,
    isLoading: instanceId ? isLoading(instanceId) : false,
    hasError: instanceId ? hasError(instanceId) : false,
    refresh: refreshCurrentInstance
  };
};
